/**
 * THE ONE PLACE THE SYSTEM'S VOCABULARY IS WRITTEN DOWN (P22)
 *
 * =========================================================================
 * WHY
 * =========================================================================
 * Every screen up to now showed the DATABASE's word for a thing. `NeedsReview`,
 * `AuthCustomerUnresolved`, `DraftPlaceholderUnfilled` — accurate, and unusable
 * by anyone who did not write the pipeline. The review meeting on 2026-08-31
 * spent real minutes decoding `AuthorizationRequestExpired` out loud.
 *
 * Worse, the words were scattered: status lists were hard-coded in six files,
 * and the only label map in the whole SPA was `REASON_LABEL` in
 * `Authorizations.tsx` (four authorization-request reasons). Two screens could
 * disagree about what a state means and nothing would notice.
 *
 * So: one module. Every status, every review reason, every draft state gets a
 * LABEL (what it is, in English — the company language) and a TOOLTIP written
 * in PROCESS WORDS: what actually has to happen next, and who does it. Not a
 * translation of the code — an instruction.
 *
 * =========================================================================
 * TWO RULES FOR EDITING THIS FILE
 * =========================================================================
 * 1. **The key is the raw DB string.** `Dra.Tickets.ReviewReason` is a
 *    `'; '`-joined list of codes; the audit trail's action names are DIFFERENT
 *    strings for the same events (`DraftNoTemplate` the reason vs `NoTemplate`
 *    the audit detail). This map keys on the REASON strings, because those are
 *    what reaches the screen.
 * 2. **An unknown code falls back to itself.** A reason we forgot to label must
 *    still be readable, and a blank chip would hide work. `labelFor` never
 *    invents and never hides.
 */

export interface Labelled {
    label: string;
    tooltip: string;
}

/**
 * THE FOUR TONES A STATE MAY HAVE (P38, reference §8)
 *
 * Blue is *the agent has it and nobody is waiting on a person*, amber is *a
 * person has to act*, green is *finished*, and everything else is neutral. `err`
 * is the fifth and is not a state the DRA's tickets have — it belongs to the
 * draft warnings.
 *
 * This file names the TONE and never the class. The class lives in
 * `components/ui/Badge.tsx`, which is also where `StatusPill` refuses `err`. Two
 * modules, one decision each: what a state MEANS is vocabulary and belongs here
 * beside its word; what that tone LOOKS like is the kit's.
 */
export type Tone = 'neutral' | 'info' | 'warn' | 'done' | 'err';

/**
 * The glyph beside a tone. A NAME, not a component — this module imports nothing
 * (see the header), and a lucide icon is a React component. `ui/glyphs.tsx` says
 * what each name draws.
 */
export type GlyphName = 'dot' | 'question' | 'clock' | 'pause' | 'check' | 'x' | 'held';

// ---------------------------------------------------------------------------
// Tickets.Status
// ---------------------------------------------------------------------------

export const TICKET_STATUS_LABELS: Record<string, Labelled> = {
    New: {
        label: 'New',
        tooltip: 'The mail became a ticket and the agent read it cleanly. The next scheduled run checks whether the document may go out.',
    },
    NeedsReview: {
        label: 'Needs review',
        tooltip: 'The agent stopped and is asking a person. The review reason says what it is asking — answer it on the ticket.',
    },
    AwaitingSalesApproval: {
        label: 'Waiting on Sales',
        tooltip: 'This customer has no recent purchase of this product, so the responsible Sales Manager was asked to authorise it. The decision is made on Authorizations.',
    },
    AwaitingApproval: {
        label: 'Draft waiting for approval',
        tooltip: 'A reply has been written and is sitting in Approvals. Nothing goes out until somebody approves it there.',
    },
    AwaitingSupplier: {
        label: 'Waiting on the supplier',
        tooltip: 'The document has to come from the supplier before the request can be answered. The supplier loop is not automated in the pilot.',
    },
    Closed: {
        label: 'Closed',
        tooltip: 'The request is finished. The close reason says how — a delivered document, a refusal, or a duplicate.',
    },
    Cancelled: {
        label: 'Cancelled',
        tooltip: 'The request was dropped without an answer — spam, or a mail that turned out not to be a request at all.',
    },
};

/** The statuses the dropdown offers, in lifecycle order. */
export const TICKET_STATUSES = [
    'New', 'NeedsReview', 'AwaitingSalesApproval', 'AwaitingApproval', 'AwaitingSupplier', 'Closed', 'Cancelled',
];

/**
 * THE TINT PER STATUS (P38, task 1 — §8's table, which named P38 as its owner)
 *
 * =========================================================================
 * WHAT THIS REPLACES, AND WHY IT WAS WRONG
 * =========================================================================
 * Until P38 this map held Tailwind classes and five different colour families:
 * `blue-50`, `amber-50`, **`purple-50` twice**, `orange-50`, `gray-100`. Purple
 * and orange are in no reference — they are not in §2's token table and they mean
 * nothing, so a reader learning the board had to learn five colours and was
 * rewarded for two of them.
 *
 * §8 allows three tints and neutral, and assigns them by ONE question: who is
 * waiting? `AwaitingSalesApproval` and `AwaitingApproval` are neutral here
 * because the waiting is somebody else's desk — `/authorizations` and
 * `/approvals` are where those become amber, on the screen where the person who
 * must act is the person reading. `Closed` is the only green status. `Cancelled`
 * is grey and not red: a decision, not an error.
 *
 * =========================================================================
 * AND WHAT `/review` DOES DIFFERENTLY (the phase's core)
 * =========================================================================
 * The audit measured **103 amber-tinted elements on one `/review` screen** and
 * the diagnosis was in the count: the status chip, every review-reason chip and
 * the idle badge shared one tone, so amber told a reader nothing about which row
 * to open.
 *
 * On `/review` every row needs a person by construction — that is what the page
 * IS — so amber on the status chip distinguishes no row from any other. What
 * varies is WHICH question, and that is the reason chip. So `ticketStatusTone`
 * takes a `board` and answers `neutral` for `NeedsReview` there.
 *
 * **§8's stated reason for this is wrong and the rule is still right.** §8 says
 * *"on `/review` every row is `NeedsReview` by construction, so the status chip
 * repeats the page"* — that has not been true since P31 made the queue open work:
 * the board legitimately shows `New`, `AwaitingApproval` and others that carry a
 * leftover question (`DRA-2026-00003`, `DRA-2026-00025`). The chip there is not
 * redundant; it is simply not the question. Amber is for the question.
 */
/**
 * The four tints §8 allows a STATUS to have. `err` is the fifth `Tone` and is
 * not one of them — it is the draft-warning marker, and an error is not a state
 * a ticket is in.
 *
 * P39 made this a TYPE rather than a guard. Until then `ticketStatusTone`
 * returned the whole union and every caller of `StatusPill` — which refuses
 * `err` — had to write `tone === 'err' ? 'neutral' : tone` for itself. Two call
 * sites had it, the board and the ticket header, which is one rule kept in two
 * places and therefore one rule that can drift. `p38:verify` asserts the VALUES
 * independently, so the two agree by construction now.
 */
export type StatusTone = Exclude<Tone, 'err'>;

export const TICKET_STATUS_TONE: Record<string, StatusTone> = {
    New: 'info',
    NeedsReview: 'warn',
    AwaitingSalesApproval: 'neutral',
    AwaitingApproval: 'neutral',
    AwaitingSupplier: 'neutral',
    Closed: 'done',
    Cancelled: 'neutral',
};

export const TICKET_STATUS_GLYPH: Record<string, GlyphName> = {
    New: 'dot',
    NeedsReview: 'question',
    AwaitingSalesApproval: 'clock',
    AwaitingApproval: 'clock',
    AwaitingSupplier: 'pause',
    Closed: 'check',
    Cancelled: 'x',
};

/**
 * The side effect of moving a ticket back to `New` that nothing used to state.
 *
 * Step 9b (`compose-pending`) sweeps tickets whose authorization was settled
 * after their mail was processed. A ticket parked on `New` with a settled
 * authorization is exactly what it looks for, so setting this status can
 * produce a draft within five minutes without anybody pressing compose.
 */
export const STATUS_SIDE_EFFECTS: Record<string, string> = {
    New: 'Careful: the agent sweeps New tickets every five minutes. If this one\'s authorization is already settled, it will write a reply draft on its own — it lands in Approvals, and nothing is sent without an approval.',
};

// ---------------------------------------------------------------------------
// Tickets.CloseReason
// ---------------------------------------------------------------------------

export const CLOSE_REASON_LABELS: Record<string, Labelled> = {
    Delivered: { label: 'Document delivered', tooltip: 'The customer was sent the document they asked for.' },
    NotAuthorized: { label: 'Not authorised', tooltip: 'Sales decided this customer may not have this document.' },
    DocumentUnavailable: { label: 'No document exists', tooltip: 'There is no such document in the official archive, and the customer was told so.' },
    Duplicate: { label: 'Duplicate request', tooltip: 'Another ticket already covers this request and answers it.' },
    Spam: { label: 'Not a real request', tooltip: 'The mail was not a document request at all.' },
    Other: { label: 'Other', tooltip: 'Closed for a reason none of the others describe — say which in a note on the ticket.' },
};

export const CLOSE_REASONS = ['Delivered', 'NotAuthorized', 'DocumentUnavailable', 'Duplicate', 'Spam', 'Other'];

// ---------------------------------------------------------------------------
// Tickets.AuthorizationStatus
// ---------------------------------------------------------------------------

export const AUTHORIZATION_STATUS_LABELS: Record<string, Labelled> = {
    Pending: { label: 'Not checked yet', tooltip: 'The purchase check has not run for this ticket yet.' },
    NotFound: {
        label: 'Could not be checked',
        tooltip: 'The check could not run — usually because the customer or the article is not settled. Answer that first; the check is re-run automatically when you do.',
    },
    Purchased: {
        label: 'Bought recently',
        tooltip: 'This customer bought this product family within the last 24 months, so the document may go out without asking anyone.',
    },
    AwaitingSalesApproval: {
        label: 'Waiting on Sales',
        tooltip: 'No recent purchase, so a Sales Manager was asked. Decided on Authorizations.',
    },
    SalesApproved: { label: 'Sales said yes', tooltip: 'A Sales Manager authorised this request.' },
    SalesRejected: { label: 'Sales said no', tooltip: 'A Sales Manager refused this request. The reply, if any, is a refusal.' },
    Override: { label: 'Overridden by Regulatory', tooltip: 'Regulatory decided to proceed without the purchase evidence. The decision is in the audit trail with a name on it.' },
    NotRequired: { label: 'No check needed', tooltip: 'This document type does not require a purchase check.' },
};

// ---------------------------------------------------------------------------
// OutboundDrafts.Status / DraftType
// ---------------------------------------------------------------------------

export const DRAFT_STATUS_LABELS: Record<string, Labelled> = {
    PendingApproval: { label: 'Waiting for approval', tooltip: 'Written, not sent. Somebody with edit rights on Approvals has to approve it.' },
    Approved: { label: 'Approved', tooltip: 'Approved. If it has not gone out, the recipient is outside the allowed domains — that block is deliberate.' },
    Sent: { label: 'Sent', tooltip: 'The mail left the building. The delivery proof on the ticket names what went, to whom and when.' },
    Rejected: { label: 'Rejected', tooltip: 'Somebody refused this draft. The ticket goes back to review.' },
    Failed: { label: 'Send failed', tooltip: 'Approved, but the mail could not be sent. The audit trail carries the error.' },
    Superseded: {
        label: 'Withdrawn — facts changed',
        tooltip: 'Somebody corrected the customer, the article or the language on the ticket after this draft was '
            + 'written, so it was withdrawn and a new reply composed from the corrected facts. Nobody refused the '
            + 'wording — that is "Rejected". Nothing was sent, nothing was deleted: the text and the document are '
            + 'still on this row, and the audit trail names the fact that changed and who changed it.',
    },
};

// ---------------------------------------------------------------------------
// The warnings on a draft (P24)
// ---------------------------------------------------------------------------

/**
 * What each warning code means, and what a person does about it.
 *
 * The SERVER writes the sentence (`warnings.ts` produces `message`, already
 * phrased for a reader) — this map supplies the short label for the chip and
 * the tooltip that says what the next move is. Two halves of one statement, and
 * the reason the message itself is not duplicated here: it names the actual
 * file, language and product of the draft in front of you, which a static map
 * cannot.
 */
export const DRAFT_WARNING_LABELS: Record<string, Labelled> = {
    BodyEmpty: {
        label: 'No text written',
        tooltip: 'This reply has no words in it. A hand-written reply starts empty on purpose — open the draft, '
            + 'type what the customer should be told and save it. Until then it cannot be part of a bulk '
            + 'approval, and approving it alone would send a blank mail.',
    },
    ProductNameMismatch: {
        label: 'Wrong product?',
        tooltip: 'The attached file name does not contain the product that was asked for, under either the '
            + 'customer\'s wording or the ERP name. The search matches the product FAMILY, so this is how a '
            + 'different chemical\'s data sheet reaches a customer. Switch the document, or confirm it yourself.',
    },
    ProductGradeMismatch: {
        label: 'Wrong grade?',
        tooltip: 'The attached file names the product FAMILY but not the grade that was asked for — '
            + '"ACTICIDE MBS 5050" where "Acticide MBS" was requested. A grade is a different chemical with '
            + 'a different data sheet. Search the archive for the sheet filed under the exact name, or '
            + 'confirm this one yourself.',
    },
    BodyNamesMissingDocument: {
        label: 'Text names another file',
        tooltip: 'The mail names a PDF that is not the one attached — either the attachment was changed after '
            + 'the text was written, or the document was removed and the sentence promising it left standing. '
            + 'The customer would be told to look for a file that is not there.',
    },
    DocumentLanguageMismatch: {
        label: 'Language not signed for',
        tooltip: 'The attached document is not in the language this request is owed, and nobody has put their '
            + 'name to that. Choose a document in the owed language, or accept the mismatch explicitly — the '
            + 'acceptance is recorded with your name.',
    },
    DocumentLanguageOverridden: {
        label: 'Language override signed',
        tooltip: 'Somebody accepted delivering this document in a language the customer is not owed, and the '
            + 'audit trail names them. It can still be sent; it cannot be sent in a batch without being read.',
    },
    NoDocumentAttached: {
        label: 'No document',
        tooltip: 'This reply promises a document and carries none. Choose one from the archive, or reject the '
            + 'draft so a refusal can be written instead.',
    },
    RecipientNotAllowed: {
        label: 'Blocked recipient',
        tooltip: 'A recipient is outside the domains the pilot may mail. Approving records the approval and the '
            + 'mail does NOT go out — that block is deliberate until the pilot goes live.',
    },
};

export const DRAFT_TYPE_LABELS: Record<string, Labelled> = {
    AckPlusDelivery: { label: 'Answer with document', tooltip: 'Confirms the request and carries the document as an attachment.' },
    Correction: {
        label: 'Correction',
        tooltip: 'A second mail in the same thread, because the first answer was wrong. It was started by a person, '
            + 'carries the wording they edited, and goes through the ordinary approval like any other reply.',
    },
    Custom: {
        label: 'Written by hand',
        tooltip: 'A reply a person started themselves on a ticket the agent left unanswered — no template, no '
            + 'wording from the system. It starts empty and goes through the ordinary approval like any other reply.',
    },
    Delivery: { label: 'Document only', tooltip: 'Sends the document without a separate acknowledgement.' },
    Acknowledgement: { label: 'Acknowledgement', tooltip: 'Tells the customer the request arrived; the document follows separately.' },
    NegativeReply: { label: 'Refusal', tooltip: 'Tells the customer the document will not be sent, and why.' },
};

// ---------------------------------------------------------------------------
// AuthorizationRequests.Reason — absorbed from Authorizations.tsx (P22)
// ---------------------------------------------------------------------------

export const AUTHORIZATION_REASON_LABELS: Record<string, Labelled> = {
    OutsideLookback: { label: 'purchased more than 24 months ago', tooltip: 'There is a purchase on file, but it is older than the 24-month window the ladder uses.' },
    NoPurchaseHistory: { label: 'no purchase of this product on file', tooltip: 'This customer buys from us, but never this product family.' },
    Prospect: { label: 'no purchase history at all', tooltip: 'This customer has never bought anything — a prospect, not a customer.' },
    UnknownSender: { label: 'sender could not be resolved to a customer', tooltip: 'Nobody knows who asked, so nobody can say whether they may have it.' },
};

// ---------------------------------------------------------------------------
// Tickets.ReviewReason — the whole reachable set
// ---------------------------------------------------------------------------

/**
 * Grouping mirrors `api/src/functions/tickets/review-reasons.ts`, which is what
 * the `reviewReasonFamily` filter sends. If you add a code, add it in BOTH — a
 * code in only one place is either an unfilterable reason or an unlabelled one.
 */
export const REVIEW_REASON_FAMILIES = {
    customer: [
        'CustomerUnresolved', 'CustomerAmbiguous', 'CustomerNeedsConfirmation',
        'LowConfidence:customer', 'AuthCustomerUnresolved',
    ],
    product: [
        'ProductUnresolved', 'ProductAmbiguous', 'ProductNeedsConfirmation',
        'ProductScopeAllPurchased',
        'LowConfidence:product', 'AuthProductUnresolved',
    ],
    language: [
        'LowConfidence:language', 'LanguageUnsupported',
        'DraftDocumentLanguageUnavailable', 'ReplyLanguageUnavailable',
    ],
    authorization: [
        'AuthOutOfPilotScope', 'AuthSalesManagerUnresolved', 'AuthRouteToRegulatory', 'AuthDenied',
        'AuthorizationRequestExpired', 'AuthorizationRejectedBySales',
    ],
    draft: ['DraftNoTemplate', 'DraftPlaceholderUnfilled', 'DraftComposeFailed', 'DocumentNotFound', 'SupplierLoopNotBuilt'],
    // P37a. DERIVED, never stored — the ticket DTO carries it in
  // `derivedReviewReasons` and `api/src/shared/send-state.ts` decides it. Its own
  // family and not `draft`'s: "the reply could not be written" is the opposite of
  // what happened, and a person filtering for missing templates should not find
  // somebody else's finished work in the list.
  send: ['ApprovedNotSent'],
  duplicate: ['PotentialDuplicate'],
    request: ['RelayOriginUnclear', 'DocTypeAmbiguous', 'LowConfidence:docType'],
} as const;

export type ReviewReasonFamily = keyof typeof REVIEW_REASON_FAMILIES;

export const REVIEW_REASON_FAMILY_LABELS: Record<ReviewReasonFamily, Labelled> = {
    customer: { label: 'Who is the customer?', tooltip: 'The ticket has no settled customer, or the agent was not sure which one it is.' },
    product: { label: 'Which article?', tooltip: 'The ticket has no settled article, or the agent was not sure which one is meant.' },
    language: { label: 'Language', tooltip: 'The requested language is unclear, unsupported, or no document exists in it.' },
    authorization: { label: 'May it go out?', tooltip: 'The purchase check could not settle it — Sales was asked, refused, or the request ran out of time.' },
    draft: { label: 'Reply could not be written', tooltip: 'The template or the document was missing, so no reply was composed.' },
    send: {
    label: 'Approved but not sent',
    tooltip: 'Somebody approved the reply and the send was held — the recipient is outside the domains the '
      + 'pilot may mail, or sending is switched off. Nothing failed; nothing left the building either.',
  },
  duplicate: { label: 'Probably a duplicate', tooltip: 'Another open ticket already covers this exact request.' },
    request: { label: 'What was asked?', tooltip: 'The document type, or whose request a forwarded mail actually is, is unclear.' },
};

export const REVIEW_REASON_FAMILY_KEYS = Object.keys(REVIEW_REASON_FAMILIES) as ReviewReasonFamily[];

export const REVIEW_REASON_LABELS: Record<string, Labelled> = {
    // --- the reply is approved and still here (P37a) ------------------------
    ApprovedNotSent: {
        label: 'Approved, not sent',
        tooltip: 'The reply was read and approved, and the send was held by the recipient allowlist — nothing '
            + 'failed, and nothing went to the customer. This ticket is in review because only a person can '
            + 'decide what happens next: widen the allowlist, correct the address, or return the draft to the '
            + 'queue. It leaves this list by itself the moment the mail goes out, the draft is returned, or a '
            + 'corrected reply replaces it.',
    },
    // --- who is the customer -------------------------------------------------
    CustomerUnresolved: {
        label: 'Customer not found',
        tooltip: 'Nothing in the mail matched a customer in the pilot scope. Name the customer on the ticket — the whole chain is then re-run.',
    },
    CustomerAmbiguous: {
        label: 'Several customers match',
        tooltip: 'More than one customer matched the name in the mail — usually the same name in both dit and dch. Pick the right one on the ticket.',
    },
    CustomerNeedsConfirmation: {
        label: 'One customer to confirm',
        tooltip: 'No customer matched exactly, but one name in the ERP is close enough — and far enough ahead of the next one — that the agent is confident. It did NOT set it: only an exact name or an unambiguous sender domain does that. Confirm the highlighted row on the ticket, or pick another.',
    },
    'LowConfidence:customer': {
        label: 'Unsure it read the customer name',
        tooltip: 'The model was not confident it had read the customer name correctly. Confirm or correct it on the ticket.',
    },
    AuthCustomerUnresolved: {
        label: 'Purchase check has no customer',
        tooltip: 'The purchase check could not run because the customer is not settled. Answering the customer question re-runs it automatically.',
    },

    // --- which article -------------------------------------------------------
    ProductUnresolved: {
        label: 'Article not found',
        tooltip: 'The product named in the mail matched nothing in this company. Name the article on the ticket.',
    },
    ProductAmbiguous: {
        label: 'Several articles match',
        tooltip: 'The product name matched more than one family — dit holds "RUETASOLV DI***" and "RUETASOLV-DI" as two. Pick the right one on the ticket.',
    },
    ProductNeedsConfirmation: {
        label: 'One article to confirm',
        tooltip: 'No article matched exactly, but one family in this company is close enough — and far enough ahead of the next one — that the agent is confident. It did NOT set it. Confirm the highlighted row on the ticket, or pick another.',
    },
    ProductScopeAllPurchased: {
        label: 'Asked for all their products',
        tooltip: 'The mail asks for the documents of everything this customer buys, so there is no single article to look up. Pick the product families off the customer\'s own purchase history on the ticket — each pick becomes its own ticket and runs on by itself.',
    },
    'LowConfidence:product': {
        label: 'Unsure it read the article',
        tooltip: 'The model was not confident it had read the product name correctly. Confirm or correct it on the ticket.',
    },
    AuthProductUnresolved: {
        label: 'Purchase check has no article',
        tooltip: 'The purchase check is asked about ONE article and there is none yet. Setting the article re-runs it.',
    },

    // --- language ------------------------------------------------------------
    'LowConfidence:language': {
        label: 'Unsure which language was asked for',
        tooltip: 'The model was not confident about the requested language. The document language decides what may legally be delivered, so a person confirms it.',
    },
    LanguageUnsupported: {
        label: 'Language outside the pilot',
        tooltip: 'The request names a language the pilot does not cover (DE/EN/FR/IT/ES). Regulatory decides how to answer.',
    },
    DraftDocumentLanguageUnavailable: {
        label: 'No document in that language',
        tooltip: 'The archive holds no document in the requested language. Either file the missing version and compose again, or use the red override to send another language — with your name on it.',
    },
    ReplyLanguageUnavailable: {
        label: 'Unclear which language to write in',
        tooltip: 'The mail named no language, nothing usable could be read from the language it was written in, and the customer country has no single market language. Nobody chose, so the agent did not. Setting the customer often answers it; otherwise reply in English with your name on it.',
    },

    // --- may it go out -------------------------------------------------------
    AuthOutOfPilotScope: {
        label: 'Outside the pilot scope',
        tooltip: 'The customer or company is not in the pilot (dit/dch, SPC). Nothing is answered automatically outside it.',
    },
    AuthSalesManagerUnresolved: {
        label: 'No Sales Manager found',
        tooltip: 'There is nobody in the ERP to ask for this customer and company, so the request could not be sent. Regulatory decides.',
    },
    AuthRouteToRegulatory: {
        label: 'Sent to Regulatory by rule',
        tooltip: 'The ladder deliberately hands this case to Regulatory rather than to Sales.',
    },
    AuthDenied: {
        label: 'Refused by the ladder',
        tooltip: 'The authorization ladder refused the request outright.',
    },
    AuthorizationRequestExpired: {
        label: 'Sales never answered in time',
        tooltip: 'A Sales Manager was asked and the request ran out of time. Asking again is a fresh request — it re-runs the whole check.',
    },
    AuthorizationRejectedBySales: {
        label: 'Sales said no',
        tooltip: 'A Sales Manager refused. This is an ANSWER, not a machine guess, so nothing clears it automatically — the refusal reply is in Approvals.',
    },

    // --- the reply itself ----------------------------------------------------
    DraftNoTemplate: {
        label: 'No approved template',
        tooltip: 'There is no active message template for this document type in this language. Write or activate one under Masters — activating it picks the waiting tickets up by itself — or reply in English with your name on it.',
    },
    DraftPlaceholderUnfilled: {
        label: 'Template has empty placeholders',
        tooltip: 'The template contains fields the ticket could not fill, so no half-written mail was created. Fix the template under Masters.',
    },
    DraftComposeFailed: {
        label: 'Writing the reply failed',
        tooltip: 'Composing the reply hit an error. The audit trail on this ticket carries what went wrong.',
    },
    DocumentNotFound: {
        label: 'No document in the archive',
        tooltip: 'The search found no document for this article at all. File it in the official archive, then compose again.',
    },
    SupplierLoopNotBuilt: {
        label: 'Needs the supplier',
        tooltip: 'This case would have to be asked of the supplier, and that loop is not automated in the pilot. Handle it by hand.',
    },

    // --- duplicate -----------------------------------------------------------
    PotentialDuplicate: {
        label: 'Probably a duplicate',
        tooltip: 'Another OPEN ticket covers the same customer, article and document type, so this one was held: nothing was authorised and nothing was drafted for it. Decide it on the ticket — merge the two, or say they are different requests.',
    },

    // --- what was asked ------------------------------------------------------
    RelayOriginUnclear: {
        label: 'Unclear whose request this is',
        tooltip: 'A colleague forwarded this mail and the agent could not tell which customer it originally came from. Name the customer on the ticket.',
    },
    DocTypeAmbiguous: {
        label: 'Not clearly an SDS request',
        tooltip: 'The pilot answers safety data sheets only, and this request may be for something else. A person decides.',
    },
    'LowConfidence:docType': {
        label: 'Unsure which document was asked for',
        tooltip: 'The model was not confident about the document type. Confirm it before anything is sent.',
    },
};

// ---------------------------------------------------------------------------
// Where the covering mail's language came from (P23)
// ---------------------------------------------------------------------------

/**
 * `replyLanguageSource`, as it appears on every `DraftComposed` / `DraftSkipped`
 * audit row and on the ticket screen's language panel.
 *
 * It exists because the ledger could not tell "the customer asked for English"
 * from "nobody said, so English was assumed" — and until 2026-09-01 the second
 * was the ORDINARY case: 25 of 47 tickets carry no requested language at all,
 * and every one of them was answered in English by a silent fallback. The
 * source is now a word on the screen as well as a key in the ledger.
 */
export const REPLY_LANGUAGE_SOURCE_LABELS: Record<string, Labelled> = {
    Requested: {
        label: 'the customer asked for it',
        tooltip: 'The mail named the language it wanted. That is honoured, or the ticket comes to you — nothing is substituted.',
    },
    MessageLanguage: {
        label: 'the language the mail was written in',
        tooltip: 'The mail did not name a language, so the reply follows the language the customer wrote in. Only used when the agent was confident enough about that reading.',
    },
    DestinationMarket: {
        label: 'the market the customer is in',
        tooltip: 'Neither asked for nor readable from the mail, so the reply follows the language that country is served in.',
    },
    EnglishOverride: {
        label: 'English, because a person decided so',
        tooltip: 'The rule pointed at another language and no wording exists in it. Somebody signed for answering in English instead — their name is in the audit trail.',
    },
    Undetermined: {
        label: 'nothing could be derived',
        tooltip: 'No language was asked for, none could be read from the mail, and the country has no single market language. The ticket stopped here rather than guess.',
    },
};

// ---------------------------------------------------------------------------
// The match score (P25)
// ---------------------------------------------------------------------------

/**
 * The three bands, in words.
 *
 * The NUMBER and the BAND both come from the server
 * (`api/src/shared/match-score.ts`). Nothing here re-derives either — this file
 * supplies the wording, exactly as it does for every status and review reason.
 *
 * The tooltips say what a person may do with each band, because the thing that
 * is easy to get wrong is not the colour but the authority: even a green score
 * has set NOTHING. Only an exact name or an unambiguous sender domain resolves a
 * ticket by itself (Roman, decision 2, 2026-09-01).
 */
export const MATCH_BAND_LABELS: Record<string, Labelled> = {
    green: {
        label: 'Confident match',
        tooltip: 'The name is close enough AND far enough ahead of the next candidate that one click settles it. '
            + 'The agent has NOT set it — a fuzzy match never resolves a ticket on its own.',
    },
    amber: {
        label: 'Possible match',
        tooltip: 'Close, but not close enough to preselect — or a second candidate scores almost as well. '
            + 'Read both names before you pick one.',
    },
    red: {
        label: 'Weak match',
        tooltip: 'Offered only because nothing better was found. Usually the sign that the customer or article '
            + 'is not in the pilot scope at all.',
    },
};

/**
 * The band as a TONE, not as a class (P38).
 *
 * It used to name `green-50 / amber-50 / red-50` with a `ring` at 20 % alpha — three
 * families outside §2's tokens and a border notation nothing else in the app
 * uses. The bands themselves are unchanged: they come from
 * `api/src/shared/match-score.ts` and nothing here re-derives them (invariant 6).
 *
 * This is the one place §8 licenses a three-step SCALE rather than a state, and
 * the vocabulary is deliberately the same: a red band means the same thing a red
 * warning does — do not act on this — and an amber band means the same thing an
 * amber reason does — a person has to look. A fourth palette for "a number
 * between 0 and 100" would be a second language for one idea.
 */
export const MATCH_BAND_TONE: Record<string, Tone> = {
    green: 'done',
    amber: 'warn',
    red: 'err',
};

// ---------------------------------------------------------------------------
// Lookup helpers — never invent, never hide
// ---------------------------------------------------------------------------

/** The label for a code, falling back to the raw code so nothing goes blank. */
export function labelFor(map: Record<string, Labelled>, code: string | null | undefined): string {
    if (!code) return '—';
    return map[code]?.label ?? code;
}

/** The tooltip for a code. Falls back to the raw code, which is still the truth. */
export function tooltipFor(map: Record<string, Labelled>, code: string | null | undefined): string {
    if (!code) return '';
    return map[code]?.tooltip ?? `Raw code from the database: ${code}. It has no description yet.`;
}

/** `ReviewReason` is a `'; '`-joined string, not a single value. */
export function reviewReasonList(reviewReason: string | null | undefined): string[] {
    return (reviewReason ?? '').split(';').map(r => r.trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// P37a — approved is not sent
// ---------------------------------------------------------------------------

/**
 * The review reason a held send derives to. Mirrors
 * `api/src/shared/send-state.ts::HELD_SEND_REVIEW_REASON`; `p22:verify` goes red
 * if the two family tables drift, and `p37a:verify` goes red if this constant does.
 */
export const HELD_SEND_REVIEW_REASON = 'ApprovedNotSent';

export const SEND_STATE_LABELS: Record<string, Labelled> = {
    None: {
        label: 'No reply written',
        tooltip: 'Nothing has been drafted for this ticket yet. The agent writes one once the authorization is '
            + 'settled, or somebody composes it here.',
    },
    Pending: {
        label: 'Waiting for approval',
        tooltip: 'A reply is written and sitting in Approvals. Nothing goes out until somebody approves it there.',
    },
    ApprovedNotSent: {
        label: 'Approved, not sent',
        tooltip: 'Somebody approved this reply and the send was held. Nothing failed — the recipient is outside '
            + 'the domains the pilot may mail, or sending is switched off. The mail is still here.',
    },
    Sent: {
        label: 'Sent',
        tooltip: 'The mail left the building. The delivery proof on this ticket names what went, to whom and when.',
    },
    Failed: {
        label: 'Send failed',
        tooltip: 'The approval happened and the send did not. The audit trail carries the error; fixing the cause '
            + 'and returning the draft to the queue is the way on.',
    },
    Rejected: {
        label: 'Reply refused',
        tooltip: 'Somebody read this reply and sent it back. Automatic drafting is paused for this ticket until a '
            + 'person composes one, corrects a fact, or moves the ticket.',
    },
};

/** The little of a ticket these three helpers need — so this module still imports nothing. */
export interface StatusSubject {
    status: string;
    sendState?: { state: string } | null;
    reviewReason?: string | null;
    derivedReviewReasons?: string[];
}

/** True when this ticket's newest reply is approved and still in the building. */
export function hasHeldSend(t: StatusSubject): boolean {
    return t.sendState?.state === 'ApprovedNotSent';
}

/**
 * THE STATUS WORD, WHICH IS NOT ALWAYS THE STATUS (P37a, task 2)
 *
 * `AwaitingApproval` reads "Draft waiting for approval", and after somebody has
 * approved the draft that sentence is false. Katia's mail of 2026-09-28 is what
 * a person concludes from it: *"ich weiss nicht, wie ich Anfragen freigeben
 * kann"* — she had, and the screen said otherwise.
 *
 * Only that one pairing is overridden. A `NeedsReview` ticket with a held send
 * keeps "Needs review", because there the review reason is the story and the
 * derived `ApprovedNotSent` chip is already in it; overriding the word there
 * would hide a question a person still has to answer.
 *
 * It is a FUNCTION and not a second entry in `TICKET_STATUS_LABELS` because the
 * word depends on two fields. Everything that prints a ticket's status goes
 * through here — the board cell, the CSV, the ticket header — so they cannot
 * disagree about one ticket.
 */
export function ticketStatusLabel(t: StatusSubject): string {
    if (t.status === 'AwaitingApproval' && hasHeldSend(t)) {
        return SEND_STATE_LABELS.ApprovedNotSent.label;
    }
    return labelFor(TICKET_STATUS_LABELS, t.status);
}

export function ticketStatusTooltip(t: StatusSubject): string {
    if (t.status === 'AwaitingApproval' && hasHeldSend(t)) {
        return SEND_STATE_LABELS.ApprovedNotSent.tooltip;
    }
    return tooltipFor(TICKET_STATUS_LABELS, t.status);
}

/**
 * The tone for that word, and the glyph that goes with it.
 *
 * `Approved, not sent` is amber, which §8 reserves for "a person has to act" —
 * exactly what this state is, and why the ticket is in `/review` at all. P37a set
 * it rather than leaving it to P38, because without it a held send is drawn
 * exactly like a draft nobody has read; **P38 styles it with the rest and does
 * not re-derive it**, which is why the override below is still one `if` and not a
 * second table.
 *
 * `board: 'review'` neutralises `NeedsReview` only — see `TICKET_STATUS_TONE`'s
 * header for why, and note what it does NOT neutralise: the held send stays amber
 * on `/review` too, because there the amber is not repeating the page. It is the
 * one row on that board where somebody must widen an allowlist or fix an address
 * rather than answer a question, and P37a's whole finding was that nothing said
 * so.
 */
export function ticketStatusTone(t: StatusSubject, board?: 'review'): StatusTone {
    if (t.status === 'AwaitingApproval' && hasHeldSend(t)) return 'warn';
    if (board === 'review' && t.status === 'NeedsReview') return 'neutral';
    return TICKET_STATUS_TONE[t.status] ?? 'neutral';
}

export function ticketStatusGlyph(t: StatusSubject): GlyphName {
    if (t.status === 'AwaitingApproval' && hasHeldSend(t)) return 'held';
    return TICKET_STATUS_GLYPH[t.status] ?? 'dot';
}

/**
 * EVERY review reason on a ticket, stored and derived, in one list (P37a).
 *
 * The stored column and the DTO's `derivedReviewReasons` are merged HERE and
 * nowhere else, because four things read a ticket's reasons — the chip on the
 * board row, the family filter (server-side), the OpsStrip counter (server-side)
 * and the CSV — and the two client-side ones must not be able to answer
 * differently. `reviewReason` itself stays the stored string, verbatim, for the
 * readers that make decisions from it (`lib/entity-state.ts`).
 */
export function allReviewReasons(t: StatusSubject): string[] {
    const stored = reviewReasonList(t.reviewReason);
    const derived = (t.derivedReviewReasons ?? []).filter(c => !stored.includes(c));
    return [...stored, ...derived];
}

/**
 * The reasons to draw BESIDE the status word, which is not always all of them.
 *
 * Measured on `DRA-2026-00053` the first time the ticket screen rendered this:
 * the header read "Approved, not sent · Test data · Approved, not sent",
 * because the status word and the derived chip are the same sentence about the
 * same fact. A screen that says one thing twice in one line teaches a reader
 * that one of the two is decoration.
 *
 * Only the reason the WORD has already stated is dropped, and only where the
 * word states it. `ReviewReasonGuide` deliberately still lists it: that is the
 * index of open questions and it carries the pointer to the Reply card, so
 * leaving it out there would remove the only place that says what to do.
 */
export function reviewReasonsBesideStatus(t: StatusSubject): string[] {
    const shown = allReviewReasons(t);
    if (ticketStatusLabel(t) !== SEND_STATE_LABELS.ApprovedNotSent.label) return shown;
    return shown.filter(r => r !== HELD_SEND_REVIEW_REASON);
}

/**
 * P37a, Roman's decision K3 — the words for a fixture ticket. The badge says
 * "Test data" and the tooltip says what that means for the reader, which is the
 * part Katia could not have known: a `.example` recipient can never receive
 * mail, so such a ticket could not have been sent whatever the allowlist said.
 */
export const TEST_DATA_LABEL: Labelled = {
    label: 'Test data',
    tooltip: 'This ticket was raised by a synthetic test mail, not by a customer. Its recipient address is a '
        + 'reserved .example domain that can never receive mail, so no reply for it can ever be sent — whatever '
        + 'the recipient allowlist says. The fixtures are left in the system on purpose; nothing is hidden.',
};

/** Which family a single review-reason code belongs to, or null if we do not know it. */
export function familyOfReason(code: string): ReviewReasonFamily | null {
    for (const family of REVIEW_REASON_FAMILY_KEYS) {
        if ((REVIEW_REASON_FAMILIES[family] as readonly string[]).includes(code)) return family;
    }
    return null;
}

// ---------------------------------------------------------------------------
// P27 — the words for one request that arrived twice
// ---------------------------------------------------------------------------

/**
 * Every phrase the duplicate story puts on a screen, in one place, for the same
 * reason as everything above it: a reviewer who learns a word on the board has
 * learned it on the ticket, and the raw code stays reachable in the `title`.
 *
 * Three DIFFERENT relations are named here and they must not blur into one:
 *
 *   - `duplicateHeld`   — the agent stopped this ticket; nothing ran for it.
 *   - `duplicateFound`  — nothing was stopped. Two tickets simply cover the
 *                         same request and nobody has decided anything (the
 *                         same-tick race, or a fact corrected after ingest).
 *   - `chased`          — no second ticket at all: the same customer wrote again
 *                         in the same thread, so the mail was linked to the
 *                         existing ticket. Bruno's D5, decided with P27: keep
 *                         linking silently, stop hiding it.
 */
export const DUPLICATE_LABELS: Record<string, Labelled> = {
    duplicateHeld: {
        label: 'Probably a duplicate',
        tooltip: 'The agent found an older open ticket for the same customer, article, document type and language, and held this one: no purchase check was run and no reply was drafted. Merge them, or say they are different requests.',
    },
    duplicateFound: {
        label: 'Same request as another ticket',
        tooltip: 'Another open ticket covers the same customer, article, document type and language. Nothing was held — the two mails arrived too close together for the agent to see the pair, or a fact was corrected afterwards. Decide it here.',
    },
    duplicateOfThis: {
        label: 'Another ticket waits on this one',
        tooltip: 'A later request for the same thing is held in review naming this ticket. Answering this one does not answer that one — merge it, or release it as a separate request.',
    },
    chased: {
        label: 'Customer chased',
        tooltip: 'The customer wrote again in the same mail thread about this ticket. The mail was added to this case rather than raising a second ticket; it is in the case file below.',
    },
    relatedOtherLanguage: {
        label: 'Related (other language)',
        tooltip: 'Same customer and same article, but the document was asked for in a different language — so it is a different document and a different reply, not a duplicate. Shown so that whoever answers one knows the other exists.',
    },
    mergedInto: {
        label: 'Merged',
        tooltip: 'This ticket was closed as a duplicate. Its mail now sits in the other ticket’s case file, and the answer goes out from there.',
    },
};

/** The two actions on a duplicate. Both are decisions a person signs for. */
export const DUPLICATE_ACTIONS: Record<'merge' | 'notDuplicate', Labelled> = {
    merge: {
        label: 'Merge into the other ticket',
        tooltip: 'Closes this ticket as a duplicate and moves its mail onto the other ticket, so the whole conversation stays in one case file. Nothing is sent.',
    },
    notDuplicate: {
        label: 'Not a duplicate, carry on',
        tooltip: 'Releases this ticket: the purchase check runs and a reply is drafted for it in its own mail thread. The decision holds for this pair — the check will not stop it against that ticket again. Nothing is sent.',
    },
};

// ---------------------------------------------------------------------------
// P30 — the table toolbox: column names, and the three states of a fact
// ---------------------------------------------------------------------------

/**
 * The column headings of the ticket table.
 *
 * They live here rather than in `TicketTable.tsx` for the reason the whole file
 * exists: `/tickets`, `/review`, a column-chooser menu, a filter chip and a CSV
 * export all name the same column, and four of those five are new since P30. A
 * heading that reads "Owner" in the table and "Assigned to" in the chooser is a
 * second vocabulary, which is exactly what this module removed.
 */
export const TICKET_COLUMN_LABELS: Record<string, Labelled> = {
    ticketNumber: { label: 'Ticket', tooltip: 'The ticket number. Chips beside it flag a duplicate, a split, a chaser mail and a ticket that has gone quiet.' },
    status: { label: 'Status', tooltip: 'Where the ticket stands in its lifecycle.' },
    reviewReason: { label: 'What is being asked', tooltip: 'The open questions on this ticket, one chip each. A ticket can carry several.' },
    priority: { label: 'Priority', tooltip: 'How urgent the request is. The agent sets Normal unless the mail says otherwise.' },
    companyCode: { label: 'Company', tooltip: 'The Dolder company the request belongs to — dit (Italy) or dch (Switzerland). It decides which ERP catalogue the customer and article are looked up in.' },
    customer: { label: 'Customer', tooltip: 'The customer the reply goes to. "Not identified" means no ERP customer is set and only the words of the mail are known.' },
    product: { label: 'Product', tooltip: 'The article the document is for. "Not identified" means no ERP article is set and only the words of the mail are known.' },
    requestedDocTypeCode: { label: 'Doc Type', tooltip: 'Which document was asked for. The pilot handles SDS only.' },
    requestedLanguage: { label: 'Lang', tooltip: 'The language the request named. A ticket that stated none shows a dash — the reply language is then derived from the market.' },
    authorizationStatus: { label: 'Authorization', tooltip: 'Whether the document may go out to this customer for this article.' },
    assignedTo: { label: 'Owner', tooltip: 'Who has taken the ticket. Nobody is assigned by default.' },
    dueAt: { label: 'Due', tooltip: 'The date the answer is owed, when the request named one.' },
    createdAt: { label: 'Created', tooltip: 'When the mail became a ticket.' },
    // P31, review board only. It is deliberately NOT "age": a ticket somebody
    // worked on yesterday is not waiting, however old it is. The clock is the
    // last change of any kind, which is the same proxy `stale-tickets.ts` uses
    // for its reminder — so the column and the reminder can never disagree.
    waitingDays: { label: 'Waiting', tooltip: 'Whole days since anything last happened on this ticket. The review queue is sorted by this, longest first. A machine touch resets it too, so it understates rather than overstates.' },
};

/**
 * The three states a customer or product cell can be in (Roman's decision 6,
 * 2026-09-02).
 *
 * Before P30 an unidentified customer looked exactly like an identified one
 * whose code happened to be missing: `NameWithCode` rendered
 * `name ?? raw ?? code` in one grey, so "MAPEI S.P.A." and the mail's own words
 * "mapei spa milano" were the same shade of nothing. Live on 2026-09-02 that
 * covered 13 open tickets with no customer and 5 with no article.
 */
export const ENTITY_STATE_LABELS: Record<'identified' | 'notIdentified' | 'toConfirm', Labelled> = {
    identified: {
        label: 'Identified',
        tooltip: 'An ERP record is set on this ticket.',
    },
    notIdentified: {
        label: 'Not identified',
        tooltip: 'No ERP record is set. What is shown are the words of the mail. Open the ticket to identify it — nothing can be authorised or drafted until it is.',
    },
    toConfirm: {
        label: 'To confirm',
        tooltip: 'An ERP record is set, but the ticket still carries an open question about it. Open the ticket to confirm or correct it.',
    },
};

/**
 * The value a column filter uses for the "not identified" bucket.
 *
 * It is a real value in the list rather than an empty one, because the cell is
 * NOT empty: it shows the mail's own words, which are different on every ticket
 * and therefore useless as a filter value. Bucketing them is what makes
 * "show me everything nobody has identified" one click.
 */
export const NOT_IDENTIFIED = '__not_identified__';

// ---------------------------------------------------------------------------
// P38 — the company, in two letters (Roman's decision K4)
// ---------------------------------------------------------------------------

/**
 * THE COMPANY COLUMN READS `CH` / `IT`, NOT `dch` / `dit`
 *
 * Katia, on the pilot: the column showed the ERP's own internal prefix. `dch`
 * and `dit` are what the surrogate ids are built from (`dch-1000001`,
 * `dit-100755`, invariant 3) and they are correct, which is exactly the trap the
 * house rule names — *"show names, not codes; the id goes small or on hover"*.
 * A reader who has not read the schema cannot tell whether `dit` is a company, a
 * division or a document type.
 *
 * Two letters and not the full name, because the full name does not fit: the
 * column is 88 px in `BASE_COLUMNS` and *Dolder Switzerland* needs three times
 * that. The country code is what a person actually means by the distinction — the
 * Swiss house and the Italian one — it is the same two letters the file-name
 * convention C10 uses for a market, and it sorts and filters identically to the
 * code it replaces because the mapping is one-to-one.
 *
 * **The code does not disappear.** It stays in the tooltip beside the full name,
 * so a reader holding a surrogate id can still tell which company it belongs to,
 * and the CSV keeps exporting the raw value (`csvValue`) because a file is read
 * by scripts as well as by people.
 */
export const COMPANY_LABELS: Record<string, Labelled> = {
    dch: {
        label: 'CH',
        tooltip: 'Dolder Switzerland (ERP company code dch). Its customers and articles are looked up in the dch catalogue; a dit article cannot be set on a dch ticket.',
    },
    dit: {
        label: 'IT',
        tooltip: 'Dolder Italia (ERP company code dit). Its customers and articles are looked up in the dit catalogue; a dch article cannot be set on a dit ticket.',
    },
};

/** `CH`, or the raw code if a third company ever appears — never blank. */
export function companyLabel(code: string | null | undefined): string {
    if (!code) return '—';
    return COMPANY_LABELS[code]?.label ?? code;
}

export function companyTooltip(code: string | null | undefined): string {
    if (!code) return 'No company is set — the ticket has neither a resolved customer nor a resolved article to derive it from.';
    return COMPANY_LABELS[code]?.tooltip ?? `Raw ERP company code from the database: ${code}.`;
}

// ---------------------------------------------------------------------------
// P40 — the template keys, in words (audit S2-16)
// ---------------------------------------------------------------------------

/**
 * WHAT EACH TEMPLATE IS FOR, BESIDE ITS KEY — NOT INSTEAD OF IT
 *
 * `/masters` showed `sds_ack_delivery`, `sds_ack_only`, `sds_not_authorized` and
 * `sds_unavailable` as card titles, in monospace, and nothing else. The audit
 * filed it S2 and added the sentence that makes it matter more than its severity:
 * *"this is the page Katia has to work in, and the top practical blocker of the
 * pilot."* Somebody who has not read `compose-draft.ts` cannot tell which of two
 * negative replies is the one Sales refused.
 *
 * **The key stays on screen.** It is what the API takes, what `chooseTemplate`
 * selects on, and what a person quotes in a mail about a template — so this is a
 * name ADDED beside the identifier, which is the house rule (*"show names, not
 * codes; the id goes small or on hover"*) rather than a replacement.
 *
 * The tooltip says **when the agent reaches for it**, because that is the thing a
 * person editing the wording needs to know and cannot see: two of these six are
 * `NegativeReply` and they are sent in completely different situations, and two
 * more are Supplier templates that the pilot never uses at all.
 */
export const TEMPLATE_KEY_LABELS: Record<string, Labelled> = {
    sds_ack_delivery: {
        label: 'Here is the document',
        tooltip: 'The delivery mail: it acknowledges the request AND carries the data sheet. This is the '
            + 'one the pilot sends most, and the only one that ever has an attachment.',
    },
    sds_ack_only: {
        label: 'We have your request',
        tooltip: 'An acknowledgement with no document — the request is understood and something still has to '
            + 'happen before it can be answered.',
    },
    sds_not_authorized: {
        label: 'Sales said no',
        tooltip: 'The refusal after a Sales Manager decided this customer may not have this document. It is '
            + 'about PERMISSION, not about the document existing.',
    },
    sds_unavailable: {
        label: 'We have no such document',
        tooltip: 'The refusal when the document does not exist in the official archive. It is about the '
            + 'DOCUMENT, not about permission — and its current wording is about having asked the '
            + 'manufacturer. No code path selects it today (P37b).',
    },
    supplier_reminder: {
        label: 'Chasing the supplier',
        tooltip: 'A reminder to a supplier who has not answered. Phase 1b — the supplier loop is not '
            + 'automated in the pilot, so this is never sent today.',
    },
    supplier_request: {
        label: 'Asking the supplier',
        tooltip: 'The first request to a supplier for a document we do not hold. Phase 1b — not sent today.',
    },
};

/** The words for a key, falling back to the key itself so nothing goes blank. */
export function templateKeyLabel(key: string | null | undefined): string {
    if (!key) return '—';
    return TEMPLATE_KEY_LABELS[key]?.label ?? key;
}

export function templateKeyTooltip(key: string | null | undefined): string {
    if (!key) return '';
    return TEMPLATE_KEY_LABELS[key]?.tooltip
        ?? `Raw template key from the database: ${key}. It has no description yet.`;
}
